import {Component, OnDestroy, ViewChild} from '@angular/core';
import {Subject, takeUntil} from "rxjs";
import {Store} from "@ngrx/store";
import * as fromRoot from "../../../../../../state/app.state";
import {
  FetchParticipantsAndCaregiversListRequest
} from "../../../core/clients-list.actions";
import {
  participantsAndCaregiversList
} from "../../../core/clients-list.selectors";
import {
  indicatorColors, participantGroupStatusColors,
  sortOrderMap, userStatusColors,
  userStatusMap
} from '../../../../../shared/constants/common.constants';
import {CommonService} from '../../../../../shared/services/common.service';
import {ClientListService} from '../../../services/client-list.service';
import {ActivatedRoute, Router} from '@angular/router';
import {
  ParticipantsAndCaregiversList
} from '../../../../../../models/participants-and-caregivers-list';
import {
  ParticipantAndCaregiver
} from '../../../../../../models/participantAndCaregiver';
import {MatSort, Sort} from "@angular/material/sort";
import {drillDownUrls} from "../../../../../shared/core/shared.selectors";
import {SetDrillDownUrls} from "../../../../../shared/core/shared.actions";
import {MatTable, MatTableDataSource} from "@angular/material/table";

@Component({
  selector: 'app-participant-or-caregiver-information-table',
  templateUrl: './participant-or-caregiver-information-table.component.html',
  styleUrls: ['./participant-or-caregiver-information-table.component.css']
})
export class ParticipantOrCaregiverInformationTableComponent implements OnDestroy {
  @ViewChild(MatTable) table: MatTable<any>;
  @ViewChild(MatSort) sort: MatSort;

  isFirstPage = true;
  sortOrder: any = 1;
  sortColumn: any = 'name'
  loading: boolean = false;
  typeMap = {
    4: 'Adult',
    5: 'Participant',
  };
  participantCaregiverList: ParticipantsAndCaregiversList;
  tableData: any[];
  dataSource: any = new MatTableDataSource();
  displayedColumns = [
    'name',
    'role',
    'status',
    'phoneNumber',
    'email',
    'gender',
    'preferredPronouns',
    'ageAtStart',
    'mostRecentAttendedSessionsGroup',
    'participantsGroupStatus',
    'mostRecentAttendedSession',
    'totalSessionsConducted',
    'ethnicity',
    'preferredLanguage',
    'race',
    'govtAssist',
    'snap',
    'schoolLunchProgram',
    'foodInsecurity',
    'housingInsecurity',
    'financialStrain',
  ];
  protected readonly userStatusMap = userStatusMap;
  private readonly OnDestroy: Subject<any> = new Subject<any>();
  private participantCaregiverFilters: {
    clientId?: number[],
    groupId?: number[],
    role?: number[],
    status?: number[],
    searchText?: string,
  } = null;

  groupId;

  constructor(
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private clientListService: ClientListService,
    private router: Router,
    private route: ActivatedRoute
  ) {

    if (this.route.snapshot.paramMap.get("groupId")) {
      this.groupId = this.route.snapshot.paramMap.get("groupId");
    }


    // fetch participant caregiver list
    this.fetchParticipantCaregiverList();

    this.store.select(participantsAndCaregiversList)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(data => {
        this.loading = data.loading;
        this.participantCaregiverList = data;

        if (!data.loading) {
          this.dataSource.data = [...data.list];

          if (data.hasNext) {
            setTimeout(() => {
              if (this.sortColumn && data.pageNumber === 1) {
                this.commonService.scrollColumnInView(this.sortColumn);
              }
            }, 0)

            setTimeout(() => {
              // check window height for infinite scroll
              this.commonService.checkInfiniteScrollHeightAndCallNext('table-wrapper', () => {
                this.onScroll();
              });
            }, 0)
          }
        }

      });

    // listen for filter changes from the filter component
    this.clientListService.onParticipantCaregiverFilterChange.pipe(takeUntil(this.OnDestroy))
      .subscribe((filters) => {
        this.participantCaregiverFilters = filters;
        this.isFirstPage = true;
        this.fetchParticipantCaregiverList();
      })

  }

  fetchParticipantCaregiverList(pageNumber = 1, limit = 10) {
    this.store.dispatch(FetchParticipantsAndCaregiversListRequest({
      payload: {
        ...this.participantCaregiverFilters,
        pageNumber,
        limit,
        ...(this.sortOrder ? {sortOrder: this.sortOrder} : {}),
        ...(this.sortOrder ? {sortColumn: this.sortColumn} : {}),
        ...(this.groupId ? {groupId: [this.groupId]} : {}),
      }
    }));
  }

  onScroll() {
    if (this.participantCaregiverList?.hasNext && !this.participantCaregiverList?.loading) {
      this.fetchParticipantCaregiverList(this.participantCaregiverList.pageNumber + 1, this.participantCaregiverList.limit);
      this.isFirstPage = false;
    }
  }

  openUserDetails(user: ParticipantAndCaregiver) {
    if (user?.id) {
      this.store.select(drillDownUrls).subscribe(data => {
        const updatedDrillDownUrls = [...data, this.router.url];
        if ([...data].pop() !== this.router.url) {
          this.store.dispatch(SetDrillDownUrls({payload: updatedDrillDownUrls}));
        }
      }).unsubscribe();

      const urlPrefix = this.router.url.split('/')[1];
      this.router.navigate([`/${urlPrefix}/member/${user.id}/behavior-tracker`]);
    }
  }

  groupNameClick(data) {
    const urlPrefix = this.router.url.split('/')[1];
    this.router.navigate([`${urlPrefix}/group/${data.latestGroupId}/dashboard`])
  }

  sessionNameClick(data) {
    const urlPrefix = this.router.url.split('/')[1];
    this.router.navigate([`/${urlPrefix}/group/${data.latestGroupId}/sessions/${data.mostRecentAttendedSession}/details`]);
  }

  sortData(sort: Sort) {
    this.isFirstPage = true;
    this.sortColumn = `${sort.active}`;
    this.sortOrder = sortOrderMap[sort.direction];
    this.fetchParticipantCaregiverList();
  }

  ngOnDestroy() {
    this.OnDestroy.next(null);
    this.OnDestroy.complete();
  }

  protected readonly userStatusColors = userStatusColors;
  protected readonly participantGroupStatusColors = participantGroupStatusColors;
}
